import { ResourceType, TraceTTLTier } from '@/types/schema';
import { appOrganizationPath } from '@/utils/constants';

export const LEGACY_PRICING_INTRODUCED_DATE = 'April 2nd';
export const STARTUP_FORM_URL =
  'https://airtable.com/app8ZrGLtHAtFVO1o/pagfLAmdTz4ep7TGu/form';
export const STARTUP_APPROVAL_VALIDITY_DAYS = 180;
export const DEFAULT_NUM_FEW_SHOT_EXAMPLES = 5;

export const V2_EVALUATOR_VERSION = 2;

type PaymentPlan = {
  pricePerSeat: number;
  maxSeats: number;
  pricePerTrace: number;
  shortLivedPricePerTrace: number;
  longLivedPricePerTrace: number;
  numFreeTraces: number;
  pricePerNodeExecution: number;
  pricePerUptimeMinuteDev: number;
  pricePerUptimeMinuteProd: number;
  // Only used for startup and premier plans.
  eligibilityYears: number;
  lgpDevDeployments?: number;
};
export type SubscribeToPlanStep = 'manage_spend' | 'add_card' | 'claim_gift';

export const PAYMENT_PLAN_PRICING_MAP: Record<string, PaymentPlan> = {
  developer: {
    pricePerSeat: 0,
    maxSeats: 1,
    pricePerTrace: 0.005,
    shortLivedPricePerTrace: 0.0005,
    longLivedPricePerTrace: 0.0045,
    numFreeTraces: 5000,
    pricePerNodeExecution: 0.001,
    pricePerUptimeMinuteDev: 0.0007,
    pricePerUptimeMinuteProd: 0.0036,
    eligibilityYears: 2,
  },
  plus: {
    pricePerSeat: 39,
    maxSeats: 10,
    pricePerTrace: 0.005,
    shortLivedPricePerTrace: 0.0005,
    longLivedPricePerTrace: 0.0045,
    numFreeTraces: 10000,
    pricePerNodeExecution: 0.001,
    pricePerUptimeMinuteDev: 0.0007,
    pricePerUptimeMinuteProd: 0.0036,
    eligibilityYears: 2,
    lgpDevDeployments: 1,
  },
  startup: {
    pricePerSeat: 19,
    maxSeats: 10,
    pricePerTrace: 0.005,
    shortLivedPricePerTrace: 0.0005,
    longLivedPricePerTrace: 0.0045,
    numFreeTraces: 30000,
    pricePerNodeExecution: 0.001,
    pricePerUptimeMinuteDev: 0.0007,
    pricePerUptimeMinuteProd: 0.0036,
    eligibilityYears: 1,
    lgpDevDeployments: 1,
  },
  startup_v0: {
    pricePerSeat: 19,
    maxSeats: 10,
    pricePerTrace: 0.002,
    shortLivedPricePerTrace: 0.0002,
    longLivedPricePerTrace: 0.0018,
    numFreeTraces: 500000,
    pricePerNodeExecution: 0.001,
    pricePerUptimeMinuteDev: 0.0007,
    pricePerUptimeMinuteProd: 0.0036,
    eligibilityYears: 2,
    lgpDevDeployments: 1,
  },
  partner: {
    pricePerSeat: 0,
    maxSeats: 10,
    pricePerTrace: 0.002,
    shortLivedPricePerTrace: 0,
    longLivedPricePerTrace: 0,
    numFreeTraces: 100000,
    pricePerNodeExecution: 0.001,
    pricePerUptimeMinuteDev: 0.0007,
    pricePerUptimeMinuteProd: 0.0036,
    eligibilityYears: 2,
    lgpDevDeployments: 1,
  },
  premier: {
    pricePerSeat: 0,
    maxSeats: 10,
    pricePerTrace: 0.002,
    shortLivedPricePerTrace: 0.0002,
    longLivedPricePerTrace: 0.0018,
    numFreeTraces: 1000000,
    pricePerNodeExecution: 0.001,
    pricePerUptimeMinuteDev: 0.0007,
    pricePerUptimeMinuteProd: 0.0036,
    eligibilityYears: 2,
    lgpDevDeployments: 1,
  },
  enterprise: {
    pricePerSeat: 0,
    maxSeats: 0,
    pricePerTrace: 0,
    shortLivedPricePerTrace: 0,
    longLivedPricePerTrace: 0,
    numFreeTraces: 0,
    pricePerNodeExecution: 0,
    pricePerUptimeMinuteDev: 0,
    pricePerUptimeMinuteProd: 0,
    eligibilityYears: 2,
  },
  enterprise_legacy: {
    pricePerSeat: 0,
    maxSeats: 0,
    pricePerTrace: 0,
    shortLivedPricePerTrace: 0,
    longLivedPricePerTrace: 0,
    numFreeTraces: 0,
    pricePerNodeExecution: 0,
    pricePerUptimeMinuteDev: 0,
    pricePerUptimeMinuteProd: 0,
    eligibilityYears: 0,
  },
  no_plan: {
    pricePerSeat: 0,
    maxSeats: 0,
    pricePerTrace: 0,
    shortLivedPricePerTrace: 0,
    longLivedPricePerTrace: 0,
    numFreeTraces: 0,
    pricePerNodeExecution: 0,
    pricePerUptimeMinuteDev: 0,
    pricePerUptimeMinuteProd: 0,
    eligibilityYears: 0,
  },
};

export const formatter = new Intl.NumberFormat('en-US', {});

export const getPlanInfos = (ttlDurations: Record<string, number>) => ({
  developer: {
    planDesc: 'Designed for hobbyists',
    planFeatures: [
      `${formatter.format(
        PAYMENT_PLAN_PRICING_MAP['developer'].numFreeTraces
      )} free traces per month, pay as you go thereafter`,
      `$${
        PAYMENT_PLAN_PRICING_MAP['developer'].shortLivedPricePerTrace * 1000
      } per 1k base traces. (${
        ttlDurations[TraceTTLTier.shortlived]
      } day retention)`,
      `Additional $${
        PAYMENT_PLAN_PRICING_MAP['developer'].longLivedPricePerTrace * 1000
      } per 1k extended traces. (${
        ttlDurations[TraceTTLTier.longlived]
      } day retention)`,
      '1 workspace',
      'Single user license',
    ],
    planPriceHead: 'Free',
    planPriceDesc: 'for one person',
  },
  plus: {
    planDesc: 'Designed for teams to collaborate',
    planFeatures: [
      `${formatter.format(
        PAYMENT_PLAN_PRICING_MAP['plus'].numFreeTraces
      )} free traces per month, pay as you go thereafter`,
      `$${
        PAYMENT_PLAN_PRICING_MAP['plus'].shortLivedPricePerTrace * 1000
      } per 1k base traces. (${
        ttlDurations[TraceTTLTier.shortlived]
      } day retention)`,
      `Additional $${
        PAYMENT_PLAN_PRICING_MAP['plus'].longLivedPricePerTrace * 1000
      } per 1k extended traces. (${
        ttlDurations[TraceTTLTier.longlived]
      } day retention)`,
      '3 workspaces',
      'Up to 10 users per organization',
    ],
    planPriceHead: `$${PAYMENT_PLAN_PRICING_MAP['plus'].pricePerSeat}/user`,
    planPriceDesc: 'per month',
  },
  startup: {
    planDesc: 'Designed for startups',
    planFeatures: [
      'Max 10 users per organization',
      `${formatter.format(
        PAYMENT_PLAN_PRICING_MAP['startup'].numFreeTraces
      )} free traces per month`,
      `$${
        PAYMENT_PLAN_PRICING_MAP['startup'].shortLivedPricePerTrace * 1000
      } per 1k base traces. (${
        ttlDurations[TraceTTLTier.shortlived]
      } day retention)`,
      `Additional $${
        PAYMENT_PLAN_PRICING_MAP['startup'].longLivedPricePerTrace * 1000
      } per 1k extended traces. (${
        ttlDurations[TraceTTLTier.longlived]
      } day retention)`,
      '3 workspaces',
    ],
    planPriceHead: `$${PAYMENT_PLAN_PRICING_MAP['startup'].pricePerSeat}/user`,
    planPriceDesc: 'per month',
  },
  startup_v0: {
    planDesc: 'Designed for startups',
    planFeatures: [
      'Max 10 users per organization',
      `${formatter.format(
        PAYMENT_PLAN_PRICING_MAP['startup_v0'].numFreeTraces
      )} free traces per month`,
      `$${
        PAYMENT_PLAN_PRICING_MAP['startup_v0'].shortLivedPricePerTrace * 1000
      } per 1k base traces. (${
        ttlDurations[TraceTTLTier.shortlived]
      } day retention)`,
      `Additional $${
        PAYMENT_PLAN_PRICING_MAP['startup_v0'].longLivedPricePerTrace * 1000
      } per 1k extended traces. (${
        ttlDurations[TraceTTLTier.longlived]
      } day retention)`,
      '3 workspaces',
    ],
    planPriceHead: `$${PAYMENT_PLAN_PRICING_MAP['startup_v0'].pricePerSeat}/user`,
    planPriceDesc: 'per month',
  },
  premier: {
    planDesc: 'Designed for our valued customers',
    planFeatures: [
      'Max 10 users per organization',
      `${formatter.format(
        PAYMENT_PLAN_PRICING_MAP['premier'].numFreeTraces
      )} free traces per month`,
      `$${
        PAYMENT_PLAN_PRICING_MAP['premier'].shortLivedPricePerTrace * 1000
      } per 1k base traces. (${
        ttlDurations[TraceTTLTier.shortlived]
      } day retention)`,
      `Additional $${
        PAYMENT_PLAN_PRICING_MAP['premier'].longLivedPricePerTrace * 1000
      } per 1k extended traces. (${
        ttlDurations[TraceTTLTier.longlived]
      } day retention)`,
      '3 workspaces',
    ],
    planPriceHead: `$${PAYMENT_PLAN_PRICING_MAP['premier'].pricePerSeat}/user`,
    planPriceDesc: 'per month',
  },
  partner: {
    planDesc: 'Designed for our valued partners',
    planFeatures: [
      'Max 10 users per organization',
      `${formatter.format(
        PAYMENT_PLAN_PRICING_MAP['partner'].numFreeTraces
      )} free traces per month.`,
      '3 workspaces',
    ],
    planPriceHead: `$${PAYMENT_PLAN_PRICING_MAP['partner'].pricePerSeat}/user`,
    planPriceDesc: 'per month',
  },
  applyStartup: {
    planDesc: 'Qualifications',
    planFeatures: ['Small team building with AI', '<$10M in investor funding'],
    planPriceHead: 'Custom',
    planPriceDesc: 'reach out for pricing',
  },
});

export const PROGRAM_NAME = {
  developer: 'Developer plan',
  partner: 'the LangSmith Partners Program',
  startup: 'LangSmith for Startups plan',
  startup_v0: 'LangSmith for Startups plan',
  premier: 'the LangSmith Premier Program',
  plus: 'Plus plan',
};

export const PROGRAM_SUBJECT = {
  partner: 'Partners',
  startup: 'Startups',
  startup_v0: 'Startups (Legacy)',
  premier: 'Premier customers',
};

export const TIERED_WORKSPACE_UPSELL = {
  free: {
    message: 'Create an organization and upgrade to Plus to get 3 workspaces',
    cta: 'Upgrade to Plus',
    ctaLink: (workspaceId) =>
      `/${appOrganizationPath}/${workspaceId}/settings/payments`,
  },
  developer: {
    message: 'Create an organization and upgrade to Plus to get 3 workspaces',
    cta: 'Upgrade to Plus',
    ctaLink: (workspaceId) =>
      `/${appOrganizationPath}/${workspaceId}/settings/payments`,
  },
  developer_legacy: {
    message: 'Create an organization and upgrade to Plus to get 3 workspaces',
    cta: 'Upgrade to Plus',
    ctaLink: (workspaceId) =>
      `/${appOrganizationPath}/${workspaceId}/settings/payments`,
  },
  startup: {
    message: 'Upgrade to the Enterprise plan to get unlimited workspaces',
    cta: 'Get in touch',
    ctaLink: 'https://www.langchain.com/contact-sales',
  },
  startup_v0: {
    message: 'Upgrade to the Enterprise plan to get unlimited workspaces',
    cta: 'Get in touch',
    ctaLink: 'https://www.langchain.com/contact-sales',
  },
  plus_legacy: {
    message: 'Subscribe to the Plus plan to get 3 workspaces',
    cta: 'Subscribe to Plus',
    ctaLink: (workspaceId) =>
      `/${appOrganizationPath}/${workspaceId}/settings/payments`,
  },
  plus: {
    message: 'Upgrade to the Enterprise plan to get unlimited workspaces',
    cta: 'Get in touch',
    ctaLink: 'https://www.langchain.com/contact-sales',
  },
  premier: {
    message: 'Upgrade to the Enterprise plan to get unlimited workspaces',
    cta: 'Get in touch',
    ctaLink: 'https://www.langchain.com/contact-sales',
  },
  default: {
    message:
      'Maximum workspaces reached. Upgrade or contact support@langchain.dev for more information.',
    cta: 'Contact Support',
    ctaLink: 'mailto:support@langchain@dev',
  },
};

export const DEFAULT_RESOURCES = {
  [`${ResourceType.Dataset}s`]: [],
  [`${ResourceType.Deployment}s`]: [],
  [`${ResourceType.Experiment}s`]: [],
  [`${ResourceType.Project}s`]: [],
  [`${ResourceType.Prompt}s`]: [],
  [`${ResourceType.Queue}s`]: [],
  [`${ResourceType.Dashboard}s`]: [],
};

export const SESSION_EVALUATOR_STARTER_CODE = `# Import supported libraries, e.g.:
# import json

def perform_eval(run):
  # run is a Run object

  # Example usages:
  # equals_str = run['outputs']['my_key'] == 'expected_value'

  # contains_str = 'expected_to_contain' in run['outputs']['my_key']

  # decoded_json = json.loads(run['outputs']['stringified_json_key'])

  score = 1
  return { "feedback_key": score }`;

export const DATASET_EVALUATOR_STARTER_CODE = `# Import supported libraries, e.g.:
# import json

def perform_eval(run, example):
  # run is a Run object
  # example is an Example object

  # Example usages:
  # equals_str = run['outputs']['my_key'] == example['outputs']['my_key']

  # contains_str = 'expected_to_contain' in run['outputs']['my_key']

  # decoded_json = json.loads(run['outputs']['stringified_json_key'])

  score = 1
  return { "feedback_key": score }`;

export const SESSION_EVALUATOR_STARTER_CODE_JS = `function performEval(run) {
  // run is a Run object

  // Example usages:
  // const equalsStr = run.outputs.my_key === 'expected_value';
  // const containsStr = run.outputs.my_key.includes('expected_to_contain');
  // const decodedJson = JSON.parse(run.outputs.stringified_json_key);

  const score = 1;
  return { "feedback_key": score };
}`;

export const DATASET_EVALUATOR_STARTER_CODE_JS = `function performEval(run, example) {
  // run is a Run object
  // example is an Example object

  // Example usages:
  // const equalsStr = run.outputs.my_key === example.outputs.my_key;

  // const containsStr = run.outputs.my_key.includes('expected_to_contain');

  // const decodedJson = JSON.parse(run.outputs.stringified_json_key);

  const score = 1;
  return { "feedback_key": score };
}`;

export const EMAIL_REGEX =
  /^[a-zA-Z0-9][\w.!#$%&'*+/=?^_`{|}~-]*@[\w.-]+\.[a-zA-Z0-9]+$/;

export const DEFAULT_CANCEL_PLAN_MESSAGE = [
  'Your plan will be canceled effective at the end of the current billing period on',
  "You may be charged for recently added seats and any traces exceeding your plan's allotment within 1-2 business days after the billing period ends.",
];

export const REMOVE_PAYMENT_METHOD_MESSAGE = [
  'Your payment method and plan will remain active until the end of your billing period on',
  'After this date, your payment method will be removed and your personal organization will have a 5k per month trace limit.',
];

export const MEMBERS_LIMIT = 50;

export const EXTENDED_STATS_RUN_SELECT = [
  'id',
  'name',
  'inputs',
  'outputs',
  'error',
  'extra',
  'status',
  'run_type',
  'reference_example_id',
  'session_id',
  'trace_id',
  'feedback_stats',
  'total_tokens',
  'prompt_tokens',
  'completion_tokens',
  'total_cost',
  'prompt_cost',
  'completion_cost',
  'prompt_token_details',
  'completion_token_details',
  'prompt_cost_details',
  'completion_cost_details',
];
